import React, { useEffect, useRef, useState } from "react";
import styles from "styles.module.css";
import { useTranslation } from "react-i18next";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addMonths } from "date-fns";
import GridCustomPage from "ui/grid/GridPage";
import OverLayLoading, { Loading } from "ui/Loading";
import {
	getRegistrationOverviewApi,
	RegistrationOverviewParams,
} from "api/registration";
import { RegistrationOverview } from "interface/registration";
import {
	lastColumItems,
	registrationOverviewGridColumns,
} from "./utils/OverviewGridColumns";
import OverviewFilter from "./utils/OverviewFilter";
import { useLocation } from "react-router";
import { useMenuItemStore } from "store/menuItem";
import { findMenuItemByPath } from "utils/helper";
import { getPageAccessApi } from "api/menu";
import { PageAccessByMenu } from "interface/user";

const RegistrationOverviewPage: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const { menuItems } = useMenuItemStore();
	const currentPath = location.pathname;

	const [pageAccess, setPageAccess] = useState<PageAccessByMenu | null>(null);
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const overlayRef = useRef<HTMLDivElement>(null);

	const [filterOptions, setFilterOptions] =
		useState<RegistrationOverviewParams>({
			registrationTimeFrom: addMonths(new Date(), -1),
			registrationTimeTo: new Date(),
		});

	useEffect(() => {
		const pageHandler = async () => {
			const menu = findMenuItemByPath(menuItems, currentPath);
			try {
				if (!pageAccess && menu) {
					const access = await getPageAccessApi(menu.id);
					setPageAccess(access);
				}
			} catch (error) {
				console.error("Error fetching page access:", error);
			}
		};
		pageHandler();
	}, [currentPath, menuItems]);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	// if (!pageAccess) {
	//     return <Loading show={true} />;
	// }

	return (
		<div className={styles.PageContainer}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("RegistrationOverview")}
				</legend>
			</div>
			<div>
				<OverviewFilter
					filterOptions={filterOptions}
					setFilterOptions={setFilterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
				/>
			</div>
			<div style={{ marginTop: 30 }}>
				<GridCustomPage
					dataKey="registrationId"
					gridColumns={registrationOverviewGridColumns({
						onViewLandingNote: (item: RegistrationOverview) => {
							// navigate to landing note filtered by registrationId if needed
						},
						onViewSalesNote: (item: RegistrationOverview) => {
							// navigate to sales note filtered by registrationId if needed
						},
					})}
					lastColWidth="160px"
					lastColBtns={lastColumItems({
						edit: () => {
							// navigate to edit page if needed
						},
					})}
					exportGridAsPDFRef={pdfRef}
					exportGridAsExcelRef={excelRef}
					apiMethod={getRegistrationOverviewApi}
					filter={filterOptions}
					itemAboveHeight={100}
					pageAccess={pageAccess}
					showDetailIcon={false}
				/>
			</div>
		</div>
	);
};

export default RegistrationOverviewPage;
